import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import WebsiteAutocomplete from "../components/WebsiteAutocomplete";
import { useWebsite } from "../context/WebsiteContext";
import SimpleTextEditor from "../components/SimpleTextEditor";

export default function WordPressToolPage() {
  const navigate = useNavigate();
  const { websites, activeWebsiteId, setActiveWebsite, createWebsite } = useWebsite();
  const [form, setForm] = useState({ name: "", url: "", description: "", status: "active" });
  const [saving, setSaving] = useState(false);

  const summary = useMemo(() => {
    return websites.reduce(
      (acc, site) => {
        acc.total += 1;
        acc[site.status] = (acc[site.status] || 0) + 1;
        return acc;
      },
      { total: 0, active: 0, monitoring: 0, archived: 0 }
    );
  }, [websites]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await createWebsite(form);
      setForm({ name: "", url: "", description: "", status: "active" });
    } finally {
      setSaving(false);
    }
  };

  const enterWorkspace = () => {
    if (!activeWebsiteId) return;
    navigate("/tools/wpa/dashboard/questionnaire");
  };

  return (
    <main className="page-grid">
      <section className="hero-card dashboard-hero tool-hero">
        <div>
          <p className="eyebrow">Tool: WordPress Website Assessment</p>
          <h3>Assess Stability, Risk, and Decision Readiness With Precision</h3>
          <p>Use this tool to run stage-wise consulting assessments with structured evidence capture and executive-grade outputs.</p>
          <ul>
            <li>Business + technical discovery framework</li>
            <li>Incident timeline and root cause intelligence</li>
            <li>Preview and AI-generated recommendation reports</li>
          </ul>
        </div>
        <div className="dashboard-select-card">
          <h4>Launch Workspace</h4>
          <WebsiteAutocomplete
            websites={websites}
            activeWebsiteId={activeWebsiteId}
            onSelectWebsite={setActiveWebsite}
            placeholder="Search websites by name or URL..."
          />
          <button type="button" className="button-link" onClick={enterWorkspace} disabled={!activeWebsiteId}>
            Enter WordPress Workspace
          </button>
        </div>
      </section>

      <section className="stats-row">
        <article className="stat-card"><p>Total Websites</p><strong>{summary.total}</strong></article>
        <article className="stat-card"><p>Active</p><strong>{summary.active}</strong></article>
        <article className="stat-card"><p>Monitoring</p><strong>{summary.monitoring}</strong></article>
        <article className="stat-card"><p>Archived</p><strong>{summary.archived}</strong></article>
      </section>

      <section className="card-modern content-two-col">
        <article>
          <h4>What This Tool Solves</h4>
          <p>Teams often struggle to connect raw technical issues to business decisions. This tool standardizes the full path: discovery, incident evidence, review, and action recommendations.</p>
          <p className="muted">Permalink: <strong>/tools/wpa</strong> | Workspace: <strong>/tools/wpa/dashboard/*</strong></p>
        </article>
        <article>
          <h4>Outputs You Can Present</h4>
          <ul>
            <li>Structured stage-wise findings</li>
            <li>Incident impact narratives</li>
            <li>Priority recommendations with rationale</li>
            <li>Consulting-ready summaries for leadership</li>
          </ul>
        </article>
      </section>

      <section className="card-modern">
        <h4>Create New Website Workspace</h4>
        <form onSubmit={submit}>
          <input required placeholder="Website name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input required placeholder="https://example.com" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <SimpleTextEditor placeholder="Business purpose and context" rows={4} value={form.description} onChange={(value) => setForm({ ...form, description: value })} ariaLabel="create-workspace-description" />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">active</option>
            <option value="monitoring">monitoring</option>
            <option value="archived">archived</option>
          </select>
          <button type="submit" disabled={saving}>{saving ? "Saving..." : "Create Website"}</button>
        </form>
      </section>
    </main>
  );
}
